import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders, getAuthEmail, logout as authLogout } from '../utils/auth.js';
import API_BASE from '../api.js';
const ADMIN_EMAILS  = ['itihassabkasakshihai@gmail.com', 'rkps7847@gmail.com'];
const fmt           = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;
const STATUS_COLOR  = { pending: '#f59e0b', approved: '#10b981', rejected: '#ef4444' };
const STATUS_BG     = { pending: '#fffbeb', approved: '#ecfdf5', rejected: '#fef2f2' };

const AdminReferrals = () => {
  const navigate = useNavigate();
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [toast, setToast]             = useState(null);
  const [processing, setProcessing]   = useState(null);
  const [filter, setFilter]           = useState('pending');
  const [noteModal, setNoteModal]     = useState(null);
  const [adminNote, setAdminNote]     = useState('');

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/referrals/withdrawals`, { headers: getAuthHeaders() });
      if (res.status === 403) { navigate('/home'); return; }
      if (!res.ok) throw new Error('Failed to load');
      const json = await res.json();
      setWithdrawals(json.withdrawals || []);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [navigate, showToast]);

  useEffect(() => {
    if (!ADMIN_EMAILS.includes(getAuthEmail())) { navigate('/home'); return; }
    fetchData();
  }, [navigate, fetchData]);

  const approve = async (id) => {
    setProcessing(id);
    try {
      const res = await fetch(`${API_BASE}/api/admin/referrals/approve`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed');
      showToast('Withdrawal approved and paid out.');
      fetchData();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setProcessing(null);
    }
  };

  const reject = async (id, notes) => {
    setProcessing(id);
    try {
      const res = await fetch(`${API_BASE}/api/admin/referrals/reject`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, admin_notes: notes }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed');
      showToast('Withdrawal rejected. Balance restored to user.');
      setNoteModal(null);
      setAdminNote('');
      fetchData();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setProcessing(null);
    }
  };

  const filtered = withdrawals.filter(w => filter === 'all' || w.status === filter);

  return (
    <>
    <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}`}</style>
    <div style={{ minHeight: '100vh', background: '#f6f8fb', fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,sans-serif", color: '#0f172a' }}>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4', border: `1px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}`, borderRadius: 12, padding: '12px 18px', fontSize: '0.84rem', fontWeight: 600, color: toast.type === 'error' ? '#991b1b' : '#166534', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', animation: 'fadeIn 0.2s ease-out', maxWidth: 340 }}>
          {toast.type === 'error' ? '⚠ ' : '✓ '}{toast.msg}
        </div>
      )}

      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #eaecf0', padding: '0 24px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={() => navigate('/home')}>
            <img src="/logo192.png" alt="AssessMonk" style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'contain' }} />
            <span style={{ fontWeight: 800, fontSize: '0.95rem' }}>AssessMonk</span>
          </div>
          <span style={{ fontSize: '0.65rem', fontWeight: 800, background: '#fef3c7', color: '#92400e', borderRadius: 6, padding: '2px 8px', border: '1px solid #fde68a', letterSpacing: '0.05em' }}>ADMIN</span>
        </div>
        <button
          onClick={() => authLogout(navigate)}
          style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', minHeight: 44 }}
          onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; }}
        >Sign out</button>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '28px 20px 60px', boxSizing: 'border-box' }}>

        <div style={{ marginBottom: 22 }}>
          <h1 style={{ margin: '0 0 4px', fontSize: '1.4rem', fontWeight: 900, letterSpacing: '-0.02em' }}>Referral Withdrawals</h1>
          <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>Review and approve payout requests from affiliates.</p>
        </div>

        {/* Summary row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Pending',  count: withdrawals.filter(w => w.status === 'pending').length,  total: withdrawals.filter(w => w.status === 'pending').reduce((s, w) => s + w.amount, 0), color: '#f59e0b', bg: '#fffbeb' },
            { label: 'Approved', count: withdrawals.filter(w => w.status === 'approved').length, total: withdrawals.filter(w => w.status === 'approved').reduce((s, w) => s + w.amount, 0), color: '#10b981', bg: '#ecfdf5' },
            { label: 'Rejected', count: withdrawals.filter(w => w.status === 'rejected').length, total: withdrawals.filter(w => w.status === 'rejected').reduce((s, w) => s + w.amount, 0), color: '#ef4444', bg: '#fef2f2' },
          ].map(s => (
            <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.color}33`, borderRadius: 14, padding: '14px 18px' }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 700, color: s.color, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>{s.label}</div>
              <div style={{ fontWeight: 900, fontSize: '1.2rem', color: '#0f172a' }}>{s.count} request{s.count !== 1 ? 's' : ''}</div>
              <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2, fontWeight: 600 }}>{fmt(s.total)} total</div>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          {['pending', 'approved', 'rejected', 'all'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ padding: '7px 16px', borderRadius: 99, border: 'none', fontWeight: 700, fontSize: '0.76rem', cursor: 'pointer', transition: 'all 0.15s', background: filter === f ? '#0f172a' : '#f1f5f9', color: filter === f ? '#fff' : '#64748b', textTransform: 'capitalize', fontFamily: 'inherit' }}>
              {f} ({withdrawals.filter(w => f === 'all' || w.status === f).length})
            </button>
          ))}
          <button onClick={fetchData}
            style={{ marginLeft: 'auto', padding: '7px 16px', borderRadius: 99, border: '1px solid #e2e8f0', background: '#fff', fontWeight: 700, fontSize: '0.76rem', cursor: 'pointer', color: '#64748b', fontFamily: 'inherit' }}>
            ↻ Refresh
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8', fontSize: '0.85rem' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '52px 0' }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>📭</div>
            <p style={{ fontWeight: 700, color: '#475569', margin: '0 0 4px' }}>No {filter === 'all' ? '' : filter} requests</p>
            <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>Nothing to show for this filter.</p>
          </div>
        ) : (
          <div style={{ background: '#fff', borderRadius: 18, border: '1px solid #eaecf0', boxShadow: '0 1px 4px rgba(15,23,42,0.05)', overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 160px 110px 150px', padding: '10px 24px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9', gap: 10 }}>
              {['User', 'Amount', 'UPI ID', 'Date', 'Actions'].map((h, i) => (
                <div key={h} style={{ fontSize: '0.64rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: i > 0 ? 'center' : 'left' }}>{h}</div>
              ))}
            </div>
            {filtered.map((w, idx) => (
              <div key={w.id} style={{ display: 'grid', gridTemplateColumns: '1fr 90px 160px 110px 150px', padding: '14px 24px', borderBottom: idx < filtered.length - 1 ? '1px solid #f8fafc' : 'none', alignItems: 'center', gap: 10, background: idx % 2 === 0 ? '#fff' : '#fafafa', animation: 'fadeIn 0.2s ease-out' }}>
                {/* User */}
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>{w.name}</div>
                  <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: 1 }}>{w.email}</div>
                  <span style={{ display: 'inline-block', marginTop: 4, fontSize: '0.63rem', fontWeight: 700, borderRadius: 6, padding: '2px 7px', background: STATUS_BG[w.status] || '#f1f5f9', color: STATUS_COLOR[w.status] || '#64748b', border: `1px solid ${STATUS_COLOR[w.status] || '#64748b'}33` }}>
                    {w.status.toUpperCase()}
                  </span>
                  {w.admin_notes && <div style={{ fontSize: '0.67rem', color: '#64748b', marginTop: 3 }}>Note: {w.admin_notes}</div>}
                </div>
                {/* Amount */}
                <div style={{ textAlign: 'center', fontWeight: 900, fontSize: '0.95rem', color: '#10b981' }}>{fmt(w.amount)}</div>
                {/* UPI */}
                <div style={{ textAlign: 'center', fontSize: '0.72rem', color: '#475569', fontFamily: 'monospace', wordBreak: 'break-all' }}>{w.upi_id || '—'}</div>
                {/* Date */}
                <div style={{ textAlign: 'center', fontSize: '0.7rem', color: '#94a3b8' }}>
                  {new Date(w.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
                {/* Actions */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
                  {w.status === 'pending' ? (
                    <>
                      <button
                        onClick={() => approve(w.id)}
                        disabled={!!processing}
                        style={{ width: '100%', padding: '7px 0', borderRadius: 8, border: 'none', background: processing === w.id ? '#e2e8f0' : '#ecfdf5', color: processing === w.id ? '#94a3b8' : '#059669', fontWeight: 700, fontSize: '0.74rem', cursor: !!processing ? 'not-allowed' : 'pointer', transition: 'background 0.15s', fontFamily: 'inherit' }}
                        onMouseEnter={e => { if (!processing) e.currentTarget.style.background = '#d1fae5'; }}
                        onMouseLeave={e => { if (!processing) e.currentTarget.style.background = '#ecfdf5'; }}
                      >✓ Approve</button>
                      <button
                        onClick={() => { setNoteModal({ id: w.id }); setAdminNote(''); }}
                        disabled={!!processing}
                        style={{ width: '100%', padding: '7px 0', borderRadius: 8, border: 'none', background: '#fef2f2', color: '#dc2626', fontWeight: 700, fontSize: '0.74rem', cursor: !!processing ? 'not-allowed' : 'pointer', transition: 'background 0.15s', fontFamily: 'inherit' }}
                        onMouseEnter={e => { if (!processing) e.currentTarget.style.background = '#fee2e2'; }}
                        onMouseLeave={e => { if (!processing) e.currentTarget.style.background = '#fef2f2'; }}
                      >✕ Reject</button>
                    </>
                  ) : (
                    <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600 }}>Processed</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>

    {/* Reject note modal */}
    {noteModal && (
      <div onClick={() => !processing && setNoteModal(null)} style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 18, padding: '24px 24px 20px', maxWidth: 380, width: '100%', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', animation: 'fadeIn 0.2s ease-out' }}>
          <h3 style={{ margin: '0 0 4px', fontWeight: 800, fontSize: '0.95rem', color: '#0f172a' }}>Reject Withdrawal</h3>
          <p style={{ margin: '0 0 16px', fontSize: '0.78rem', color: '#64748b' }}>Balance will be restored to the user's account.</p>
          <textarea
            value={adminNote}
            onChange={e => setAdminNote(e.target.value)}
            placeholder="Reason for rejection (optional)"
            rows={3}
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: '0.82rem', fontFamily: 'inherit', resize: 'vertical', outline: 'none', marginBottom: 16, color: '#0f172a' }}
            onFocus={e => e.currentTarget.style.borderColor = '#ef4444'}
            onBlur={e  => e.currentTarget.style.borderColor = '#e2e8f0'}
          />
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => !processing && setNoteModal(null)}
              style={{ flex: 1, padding: '10px 0', border: '1px solid #e2e8f0', borderRadius: 9, background: '#f1f5f9', color: '#64748b', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
            <button onClick={() => reject(noteModal.id, adminNote)} disabled={!!processing}
              style={{ flex: 1, padding: '10px 0', border: 'none', borderRadius: 9, background: processing ? '#fca5a5' : '#ef4444', color: '#fff', fontWeight: 700, fontSize: '0.82rem', cursor: !!processing ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
              {processing ? 'Rejecting…' : 'Confirm Reject'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default AdminReferrals;
